import React from "react";
import {
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Box,
  Chip,
  Typography,
} from "@material-ui/core";
import MonetaryInput from "../MonetaryInput";
import ImobilizadoAutocomplete, {
  isImobilizadoField,
} from "../KanbanBoard/ImobilizadoAutocomplete";
import { i18n } from "../../translate/i18n";

// Versão enxuta do renderField do CardModal do quadro, para o card criado a
// partir do atendimento respeitar os mesmos campos (e obrigatórios) do quadro.

// Função (e não constante de módulo) para o texto seguir o idioma em uso.
const requiredHint = () => i18n.t("createKanbanCardModal.customField.required");

// fieldOptions pode chegar como array, string JSON ou objeto
export const getFieldOptions = (field) => {
  const options = field?.fieldOptions;
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  if (typeof options === "object") return Object.values(options);
  return [];
};

// Mesma normalização do CardModal: opções sempre em array e defaultValue de
// seleção múltipla desserializado
export const normalizeFieldDefinitions = (definitions) => {
  if (!Array.isArray(definitions)) return [];

  return definitions.map((field) => {
    const normalized = { ...field, fieldOptions: getFieldOptions(field) };

    if (
      (field.fieldType === "multiselect" || field.fieldType === "checkbox") &&
      typeof field.defaultValue === "string"
    ) {
      try {
        const parsed = JSON.parse(field.defaultValue);
        normalized.defaultValue = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        normalized.defaultValue = [];
      }
    }

    return normalized;
  });
};

// Mesma regra de "não preenchido" da validação do CardModal
export const isFieldValueMissing = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  return !value;
};

const CustomFieldInput = ({
  field,
  value,
  onChange,
  error = false,
  disabled = false,
}) => {
  const label = field.fieldLabel + (field.isRequired ? " *" : "");
  const helperText = error ? requiredHint() : field.helpText;
  const options = getFieldOptions(field);
  const setValue = (next) => onChange(field.fieldName, next);

  if (isImobilizadoField(field)) {
    return (
      <ImobilizadoAutocomplete
        label={label}
        value={value || ""}
        onChange={setValue}
        error={error}
        helperText={helperText}
        placeholder={field.placeholder}
      />
    );
  }

  const textProps = {
    label,
    name: field.fieldName,
    value: value === null || value === undefined ? "" : value,
    onChange: (e) => setValue(e.target.value),
    fullWidth: true,
    variant: "outlined",
    margin: "dense",
    placeholder: field.placeholder,
    helperText,
    error,
    disabled,
  };

  const controlProps = {
    fullWidth: true,
    variant: "outlined",
    margin: "dense",
    error,
    disabled,
  };

  switch (field.fieldType) {
    case "textarea":
      return (
        <TextField
          {...textProps}
          multiline
          rows={3}
          inputProps={{ maxLength: field.validation?.maxLength || 2000 }}
        />
      );

    case "select": {
      const isValid = options.some((opt) => String(opt.value) === String(value));
      return (
        <FormControl {...controlProps}>
          <InputLabel>{label}</InputLabel>
          <Select
            name={field.fieldName}
            value={isValid ? value : ""}
            onChange={(e) => setValue(e.target.value)}
            label={label}
          >
            {options.length === 0 && (
              <MenuItem value="" disabled>
                {i18n.t("createKanbanCardModal.customField.noOptions")}
              </MenuItem>
            )}
            {options.map((opt, index) => (
              <MenuItem key={opt.value ?? index} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    case "radio":
      return (
        <FormControl
          component="fieldset"
          error={error}
          disabled={disabled}
          style={{ width: "100%", marginTop: 8, marginBottom: 4 }}
        >
          <FormLabel component="legend">{label}</FormLabel>
          <RadioGroup
            name={field.fieldName}
            value={value || ""}
            onChange={(e) => setValue(e.target.value)}
          >
            {options.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                {i18n.t("createKanbanCardModal.customField.noOptions")}
              </Typography>
            )}
            {options.map((opt, index) => (
              <FormControlLabel
                key={opt.value ?? index}
                value={opt.value}
                control={<Radio />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );

    case "multiselect": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <FormControl {...controlProps}>
          <InputLabel>{label}</InputLabel>
          <Select
            name={field.fieldName}
            multiple
            value={selected}
            onChange={(e) => setValue(e.target.value)}
            label={label}
            renderValue={(values) => (
              <Box style={{ display: "flex", flexWrap: "wrap" }}>
                {values.map((v) => {
                  const opt = options.find((o) => o.value === v);
                  return (
                    <Chip
                      key={v}
                      label={opt?.label || v}
                      size="small"
                      style={{ margin: 2 }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {options.length === 0 && (
              <MenuItem value="" disabled>
                {i18n.t("createKanbanCardModal.customField.noOptions")}
              </MenuItem>
            )}
            {options.map((opt, index) => (
              <MenuItem key={opt.value ?? index} value={opt.value}>
                <Checkbox checked={selected.indexOf(opt.value) > -1} />
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    case "checkbox": {
      const checked = Array.isArray(value) ? value : [];
      const toggle = (optValue, isChecked) =>
        setValue(
          isChecked
            ? [...checked, optValue]
            : checked.filter((v) => v !== optValue)
        );

      return (
        <FormControl
          component="fieldset"
          error={error}
          disabled={disabled}
          style={{ width: "100%", marginTop: 8, marginBottom: 4 }}
        >
          <FormLabel component="legend">{label}</FormLabel>
          <Box style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
            {options.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                {i18n.t("createKanbanCardModal.customField.noOptions")}
              </Typography>
            )}
            {options.map((opt, index) => (
              <FormControlLabel
                key={opt.value ?? index}
                control={
                  <Checkbox
                    checked={checked.includes(opt.value)}
                    onChange={(e) => toggle(opt.value, e.target.checked)}
                    name={field.fieldName}
                  />
                }
                label={opt.label}
              />
            ))}
          </Box>
          {error && (
            <FormHelperText>
              {requiredHint()} {i18n.t("createKanbanCardModal.customField.selectAtLeastOne")}
            </FormHelperText>
          )}
          {!error && field.helpText && (
            <FormHelperText>{field.helpText}</FormHelperText>
          )}
        </FormControl>
      );
    }

    case "date":
      return (
        <TextField {...textProps} type="date" InputLabelProps={{ shrink: true }} />
      );

    case "number":
      return (
        <TextField
          {...textProps}
          type="number"
          inputProps={{
            min: field.validation?.min,
            max: field.validation?.max,
            step: field.validation?.step || "any",
          }}
        />
      );

    case "email":
      return (
        <TextField
          {...textProps}
          type="email"
          inputProps={{ maxLength: field.validation?.maxLength || 255 }}
        />
      );

    case "money":
      return (
        <MonetaryInput
          name={field.fieldName}
          label={label}
          value={value || 0}
          onChange={(e) => setValue(e.target.value)}
          placeholder={field.placeholder || "R$ 0,00"}
          error={error}
          helperText={helperText}
          required={field.isRequired}
          fullWidth
          variant="outlined"
          margin="dense"
          disabled={disabled}
        />
      );

    default:
      return (
        <TextField
          {...textProps}
          inputProps={{ maxLength: field.validation?.maxLength || 255 }}
        />
      );
  }
};

export default CustomFieldInput;
